import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface MaintenanceSettings {
  enabled: boolean;
  message: string;
  start_time: string | null;
  end_time: string | null;
  allow_admins: boolean;
}

const defaultSettings: MaintenanceSettings = {
  enabled: false,
  message: 'We are working on improving the service. Please try again later.',
  start_time: null,
  end_time: null,
  allow_admins: true,
};

/**
 * Hook to check maintenance status for use in individual pages.
 * This provides defense-in-depth for pages like Auth and Index.
 * CRITICAL: Signs out users when maintenance is active to prevent auth refresh loops.
 */
export function useMaintenanceCheck() {
  const [isLoading, setIsLoading] = useState(true);
  const [isMaintenanceActive, setIsMaintenanceActive] = useState(false);
  const [settings, setSettings] = useState<MaintenanceSettings>(defaultSettings);

  useEffect(() => {
    checkMaintenance();
  }, []);

  async function checkMaintenance() {
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('value')
        .eq('key', 'maintenance_mode')
        .maybeSingle();

      if (error || !data?.value) {
        // No maintenance settings = not in maintenance
        setIsMaintenanceActive(false);
        setIsLoading(false);
        return;
      }

      const maintenanceSettings: MaintenanceSettings = {
        ...defaultSettings,
        ...(data.value as object),
      };
      setSettings(maintenanceSettings);

      // Check if enabled
      if (!maintenanceSettings.enabled) {
        setIsMaintenanceActive(false);
        setIsLoading(false);
        return;
      }

      // Check time range
      const now = new Date();
      let isActive = true;

      if (maintenanceSettings.start_time) {
        const startTime = new Date(maintenanceSettings.start_time);
        if (now < startTime) isActive = false;
      }

      if (maintenanceSettings.end_time) {
        const endTime = new Date(maintenanceSettings.end_time);
        if (now > endTime) isActive = false;
      }

      if (isActive) {
        // CRITICAL: Sign out user to stop token refresh attempts during maintenance
        try {
          await supabase.auth.signOut();
          console.log('User signed out due to maintenance mode (useMaintenanceCheck)');
        } catch (signOutError) {
          console.error('Error signing out during maintenance check:', signOutError);
        }
      }

      setIsMaintenanceActive(isActive);
      setIsLoading(false);
    } catch (err) {
      console.error('Error checking maintenance:', err);
      // FAIL-CLOSED: On error, sign out and assume maintenance is active
      try {
        await supabase.auth.signOut();
      } catch (signOutError) {
        console.error('Error signing out during maintenance error:', signOutError);
      }
      setIsMaintenanceActive(true);
      setIsLoading(false);
    }
  }

  return { isLoading, isMaintenanceActive, settings };
}
